import { useEffect } from "react";
import { useDemoExplainer } from "@/components/DemoExplainer";

/**
 * DemoActionGuard
 * --------------------------------------------------
 * Global, capture-phase click + change interceptor that blocks every
 * destructive / export / import / bulk / restore / reseed / delete /
 * email-out action across the entire demo, and pops the DemoExplainer
 * modal instead of letting the underlying handler fire.
 *
 * Implementation strategy
 *   - One listener on `document`, capture phase, runs BEFORE React
 *     event delegation, so we can call stopImmediatePropagation +
 *     preventDefault and the original onClick never runs.
 *   - Match by data-testid pattern (kebab-case is consistent across
 *     this codebase).
 *   - File input clicks (<input type="file">) are blocked unless the
 *     accept attribute is image/* (PhotoUpload field-form workflow).
 *   - tt-reset-btn (Toolbox Talk demo workflow reset) is whitelisted.
 *   - Print buttons are NOT blocked (browser print preview is harmless
 *     and showcases the print stylesheet).
 *
 * This is intentionally a NET — it catches buttons the dev didn't
 * remember to wire individually. Specific-button onClick wiring still
 * exists in AdminJobMasterPanel / CrewRecoveryPanel for resilience
 * and faster UX (no double-click flicker), but the guard catches
 * everything missed.
 */

const WHITELIST_TESTIDS = new Set([
  "tt-reset-btn",          // Toolbox Talk interactive demo reset
  "demo-explainer-close",
  "demo-explainer-ok",
]);

const WHITELIST_TESTID_INCLUDES = [
  "-input-gallery",        // PhotoUpload (field-form camera/gallery)
  "-input-camera",
  "share-",                // ShareFormDialog (QR / link share is a demo feature)
  "-cancel",
  "email-cancel",
  "email-add",
  "email-subject",
  "email-note",
  "email-recipient",
  "preset-",               // Date-range preset chips (no I/O)
  "export-start-date",     // Date inputs
  "export-end-date",
  "export-refresh",        // Just refreshes counts, no download
  "ci-email",              // Company-info email field
];

// Each pattern → feature key in DemoExplainer FEATURE_PRESETS
// Order matters: more specific first.
const PATTERNS = [
  { feature: "Backup",            re: /(^|-)(backup-(run-now|now|delete)|full-backup|hero-backup-btn|pre-deploy-backup|ops-backup-now|backup-run-now-btn|run-backup-now)/ },
  { feature: "Restore Backup",    re: /(^|-)(restore-backup|restore-confirm|restore-choose-file|restore-mode-replace|restore-file|hero-restore)/ },
  { feature: "Bulk Replacement",  re: /(^|-)(bulk-(replace|input|btn|upload|confirm|textarea|dialog)|equipment-master-bulk|job-master-bulk|master-bulk)/ },
  { feature: "System Reset",      re: /(^|-)(reseed|crew-recovery-reseed|demo-banner-reseed|system-reset|wipe|purge|seed-(database|now|reset))/ },
  { feature: "Sync Now",          re: /(^|-)sync(-|$)/ },
  { feature: "Import",            re: /(^|-)(import|upload-(btn|file)|file-upload|trench-file-input|jha-(plan-)?upload|equipment-master-bulk-input)/ },
  { feature: "Download",          re: /(^|-)(download|backup-download|source-bundle|dev-source-bundle|bundle-download)/ },
  { feature: "Export",            re: /(^|-)(export|export-csv|export-all|export-kind|export-pdf|qaqc-export|parts-export|equipment-master-export|job-master-export|calc-export)/ },
  { feature: "Send Email",        re: /(^|-)(email-send|email-btn|email-all-btn|safety-cards-email-all|safety-card-email-send|parts-order-send|send-email)/ },
  { feature: "Delete",            re: /(^|-)(delete-btn|delete-row|backup-delete|trash|remove-(row|record|item)|^delete-)/ },
  { feature: "Bulk Replacement",  re: /(^|-)(replace-(all|file)|bulk-replace)/ },
];

function matchFeature(rawTestId) {
  if (!rawTestId) return null;
  const id = String(rawTestId).toLowerCase();
  if (WHITELIST_TESTIDS.has(id)) return null;
  if (WHITELIST_TESTID_INCLUDES.some((sub) => id.includes(sub))) return null;
  for (const p of PATTERNS) {
    if (p.re.test(id)) return p.feature;
  }
  return null;
}

function findInteractive(node, maxDepth = 6) {
  let cur = node;
  let i = 0;
  while (cur && i < maxDepth) {
    if (cur.nodeType === 1) {
      const el = /** @type {HTMLElement} */ (cur);
      if (el.dataset?.testid) return el;
      const tag = el.tagName;
      if (tag === "BUTTON" || tag === "A" || tag === "INPUT") return el;
    }
    cur = cur.parentNode;
    i++;
  }
  return null;
}

export default function DemoActionGuard() {
  const { showExplainer } = useDemoExplainer();

  useEffect(() => {
    const handleClick = (e) => {
      // 1. Direct file-input click — block unless image/* (photo workflow)
      const tgt = e.target;
      if (tgt && tgt.tagName === "INPUT" && tgt.type === "file") {
        const accept = (tgt.accept || "").toLowerCase();
        const tid = (tgt.dataset?.testid || "").toLowerCase();
        const isPhoto =
          accept.includes("image/") ||
          tid.includes("-input-gallery") ||
          tid.includes("-input-camera");
        if (!isPhoto) {
          e.preventDefault();
          e.stopImmediatePropagation();
          showExplainer({ feature: "Import" });
          return;
        }
      }

      // 2. Walk up to find a testid'd interactive element
      const el = findInteractive(tgt);
      if (!el) return;

      // Find the nearest ancestor with data-testid
      let cur = el;
      let testid = null;
      let hops = 0;
      while (cur && hops < 6) {
        if (cur.dataset?.testid) {
          testid = cur.dataset.testid;
          break;
        }
        cur = cur.parentElement;
        hops++;
      }

      const feature = matchFeature(testid);
      if (!feature) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      showExplainer({ feature });
    };

    // Capture-phase native listener — runs BEFORE React's delegated handlers.
    document.addEventListener("click", handleClick, true);
    document.addEventListener("change", handleClick, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("change", handleClick, true);
    };
  }, [showExplainer]);

  return null;
}
